/**
 * src/api/real-browsers.ts
 *
 * Typed wrappers over /v1/real-browsers/* — thin, no business logic.
 * Mirrors frontend/observe/src/api/observe.ts in shape so a developer
 * who knows one knows the other.
 */

import { request, requestBinary, getApiBase, getApiKey } from './client';
import type {
  BrowserProvider,
  BrowserPool,
  BrowserInstance,
  BrowserAction,
  ProviderKind,
  BrowserKind,
  ActionKind,
  CdpTab,
  AlertRecord,
  InstanceStatus,
} from '../types';

// ── Health (unauthenticated) ────────────────────────────────────────────────

export interface HealthSnapshot {
  ok: boolean;
  ts: number;
  services: {
    mongodb: { healthy: boolean | null };
    redis:   { healthy: boolean | null };
  };
}

export async function getHealth(): Promise<HealthSnapshot> {
  // /health is mounted at the API root, not under /v1.
  return request<HealthSnapshot>('/health');
}

// ── Providers / Pools / Instances ───────────────────────────────────────────

export async function listProviders(): Promise<BrowserProvider[]> {
  const r = await request<{ providers: BrowserProvider[] }>('/v1/real-browsers/providers');
  return r.providers;
}

export async function upsertProvider(p: {
  id: string; display_name?: string; kind?: ProviderKind;
  region?: string; enabled?: boolean; notes?: string;
}): Promise<BrowserProvider> {
  const r = await request<{ provider: BrowserProvider }>('/v1/real-browsers/providers', {
    method: 'POST', body: p,
  });
  return r.provider;
}

export async function listPools(provider_id?: string): Promise<BrowserPool[]> {
  const q = provider_id ? `?provider_id=${encodeURIComponent(provider_id)}` : '';
  const r = await request<{ pools: BrowserPool[] }>(`/v1/real-browsers/pools${q}`);
  return r.pools;
}

export async function upsertPool(p: {
  id: string; provider_id: string; display_name?: string;
  browser?: BrowserKind; enabled?: boolean; vnc_endpoint?: string;
}): Promise<BrowserPool> {
  const r = await request<{ pool: BrowserPool }>('/v1/real-browsers/pools', {
    method: 'POST', body: p,
  });
  return r.pool;
}

export async function listInstances(opts: {
  provider_id?: string; pool_id?: string; status?: InstanceStatus;
} = {}): Promise<BrowserInstance[]> {
  const params = new URLSearchParams();
  if (opts.provider_id) params.set('provider_id', opts.provider_id);
  if (opts.pool_id)     params.set('pool_id',     opts.pool_id);
  if (opts.status)      params.set('status',      opts.status);
  const q = params.toString() ? `?${params}` : '';
  const r = await request<{ instances: BrowserInstance[] }>(`/v1/real-browsers/instances${q}`);
  return r.instances;
}

/** x11vnc password (desktops require VNC auth since 24 Oct 2026). */
export async function getVncPassword(): Promise<string> {
  const r = await request<{ password: string }>('/v1/real-browsers/vnc-credentials');
  return r.password;
}

export async function getInstance(id: string): Promise<BrowserInstance> {
  const r = await request<{ instance: BrowserInstance }>(
    `/v1/real-browsers/instances/${encodeURIComponent(id)}`,
  );
  return r.instance;
}

export async function setInstanceEnabled(id: string, enabled: boolean): Promise<BrowserInstance> {
  const r = await request<{ instance: BrowserInstance }>(
    `/v1/real-browsers/instances/${encodeURIComponent(id)}/toggle`,
    { method: 'POST', body: { enabled } },
  );
  return r.instance;
}

// ── Live tab list (CDP pass-through) ────────────────────────────────────────

export async function listTabs(id: string): Promise<CdpTab[]> {
  const r = await request<{ tabs: CdpTab[] }>(
    `/v1/real-browsers/instances/${encodeURIComponent(id)}/tabs`,
  );
  return r.tabs;
}

// ── Actions (CDP one-shots) ─────────────────────────────────────────────────

export async function runAction(
  id: string,
  kind: ActionKind,
  detail?: Record<string, unknown>,
): Promise<{ ok: boolean; action: BrowserAction; error?: string }> {
  return request(`/v1/real-browsers/instances/${encodeURIComponent(id)}/action`, {
    method: 'POST',
    body: { kind, detail },
  });
}

export async function fetchScreenshot(id: string): Promise<Blob> {
  return requestBinary(`/v1/real-browsers/instances/${encodeURIComponent(id)}/screenshot`);
}

export async function listActions(opts: {
  limit?: number; instance_id?: string;
} = {}): Promise<BrowserAction[]> {
  const params = new URLSearchParams();
  if (opts.limit)       params.set('limit', String(opts.limit));
  if (opts.instance_id) params.set('instance_id', opts.instance_id);
  const q = params.toString() ? `?${params}` : '';
  const r = await request<{ actions: BrowserAction[] }>(`/v1/real-browsers/actions${q}`);
  return r.actions;
}

// ── Alerts (filter to infra.chrome) ─────────────────────────────────────────

export async function listChromeAlerts(limit = 50): Promise<AlertRecord[]> {
  const r = await request<{ alerts: AlertRecord[] }>(
    `/v1/observe/alerts?limit=${limit}&category=infra.chrome`,
  );
  return r.alerts;
}

// ── WebSocket URL builders (used by VncViewer) ─────────────────────────────

/**
 * Build the wss:// URL for the VNC proxy, on this page's origin by default
 * (the /v1 Pages Function in prod, the Vite proxy in dev). Behind Teleport the
 * edge adds the identity header to the upgrade request. A stored API key is
 * still sent as ?key=… since browsers can't set headers on WebSocket upgrades.
 */
function wsKeyQuery(): string {
  const key = getApiKey();
  return key ? `?key=${encodeURIComponent(key)}` : '';
}

export function vncWsUrl(instanceId: string): string {
  const base = getApiBase() || `${window.location.protocol}//${window.location.host}`;
  const wsBase = base.replace(/^http/, 'ws');
  return `${wsBase}/v1/real-browsers/instances/${encodeURIComponent(instanceId)}/vnc${wsKeyQuery()}`;
}

export function cdpWsUrl(instanceId: string): string {
  const base = getApiBase() || `${window.location.protocol}//${window.location.host}`;
  const wsBase = base.replace(/^http/, 'ws');
  return `${wsBase}/v1/real-browsers/instances/${encodeURIComponent(instanceId)}/cdp${wsKeyQuery()}`;
}
